import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import httpClient from "../../services/httpClient";
import Logger from "../../utils/Logger";
import { useAuth } from "../../AuthContext";
import orangeSortIcon from "../../media/orange-sort-icon.svg";
import "../../styleSheet/main/admin/manageRooms/manageRooms.scss";

const EditRoom = () => {
  const { id } = useParams();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hover, setHover] = useState(false);
  const navigate = useNavigate();
  const { userData } = useAuth();

  useEffect(() => {
    if (userData.userRole !== "admin") {
      navigate("/");
    }
  }, [userData, navigate]);

  useEffect(() => {
    const fetchRoom = async () => {
      try {
        const response = await httpClient.get(`/Rooms/${id}`, {
          withAuth: true,
        });
        setFormData({
          name: response.data.name,
          email: response.data.emailAddress || response.data.email, // Backend zwraca emailAddress
        });
        setIsLoading(false);
      } catch (err) {
        Logger.error("Błąd podczas pobierania danych sali:", err);
        setErrors({
          submit: "Nie udało się pobrać danych sali",
        });
        setIsLoading(false);
      }
    };

    fetchRoom();
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Nazwa sali jest wymagana";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email jest wymagany";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Nieprawidłowy format email";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      await httpClient.put(
        `/Rooms/${id}`,
        {
          id: id,
          name: formData.name.trim(),
          email: formData.email.trim().toLowerCase(),
        },
        { withAuth: true },
      );

      Logger.log("Sala zaktualizowana pomyślnie");
      navigate("/administrator/sale-konferencyjne");
    } catch (err) {
      Logger.error("Błąd podczas aktualizacji sali:", err);
      setErrors({
        submit:
          err.response?.data?.message ||
          "Nie udało się zaktualizować sali. Spróbuj ponownie.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackClick = () => {
    navigate("/administrator/sale-konferencyjne");
  };

  if (isLoading) {
    return (
      <div className="manage-rooms-container">
        <p>Ładowanie...</p>
      </div>
    );
  }

  return (
    <div className="manage-rooms-container">
      <div className="manage-rooms-header">
        <h1>Edytuj salę konferencyjną</h1>
        <div
          className="orange-sort-icon"
          onClick={handleBackClick}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          style={{
            backgroundImage: `url(${orangeSortIcon})`,
            transform: hover ? "rotate(180deg)" : "rotate(90deg)",
            cursor: "pointer",
          }}
        />
      </div>

      <form onSubmit={handleSubmit} className="room-form">
        <div className="form-group">
          <label htmlFor="name">Nazwa sali</label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            disabled={isSubmitting}
          />
          {errors.name && <span className="error-message">{errors.name}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="email">Email</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            disabled={isSubmitting}
          />
          {errors.email && (
            <span className="error-message">{errors.email}</span>
          )}
        </div>

        {errors.submit && (
          <div className="error-message submit-error">{errors.submit}</div>
        )}

        <div className="form-actions">
          <button
            type="submit"
            className="submit-button"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Zapisywanie..." : "Zapisz zmiany"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditRoom;
